import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';

import type { Highlighter } from '@openx/types/tableHelpers.ts';
import { type HighlightPhraseProps, prepareHighlightRegExp } from '@openx/utils/lib/prepareHighlightedPhrase.ts';

import type { JSX } from 'react';

export interface MultiValueTableCellProps {
  values: string[];
  phrase?: HighlightPhraseProps;
  highlighter?: Highlighter;
  mapBy?: { [key: string]: { name: string } | undefined };
  showTooltip?: boolean;
  marginTop?: number;
}

const StyledChip = styled(Chip)`
  margin-left: ${({ theme }) => theme.spacing(1)};
  padding: 0;
  color: ${({ theme }) => theme.palette.text.primary};
  background: ${({ theme }) => theme.palette.background.grayLight};

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.palette.background.boxDarkest};
  }
`;

const StyledHighlightContainerSpan = styled('span')`
  float: left;
  max-width: 82%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export function MultiValueTableCell(props: MultiValueTableCellProps): JSX.Element | null {
  const { values: dataValues, mapBy, phrase = '', showTooltip = false, marginTop = 2 } = props;

  const values = mapBy ? dataValues.map(value => mapBy[value]?.name || value) : dataValues;
  const regexp = prepareHighlightRegExp(phrase);
  const matchedPhrase = values.find(value => value.match(regexp));
  const numberOfValues = values.length;
  const isSingleValue = numberOfValues === 1;
  const firstValue = values[0];
  const firstHighlightedValue = matchedPhrase || firstValue;
  const { highlighter = () => firstHighlightedValue } = props;

  if (isSingleValue) {
    return <StyledHighlightContainerSpan>{highlighter(firstValue)}</StyledHighlightContainerSpan>;
  }

  const tooltipTitle = showTooltip && (
    <span data-test="multi-value-tooltip-span">
      {values.slice(1).map((value, key) => (
        <p key={key}>{value}</p>
      ))}
    </span>
  );

  return (
    <>
      <StyledHighlightContainerSpan style={{ marginTop }}>
        {highlighter(firstHighlightedValue)}
      </StyledHighlightContainerSpan>
      <Tooltip title={tooltipTitle || ''}>
        <StyledChip
          size="small"
          label={showTooltip ? numberOfValues - 1 : numberOfValues}
          clickable
          data-test="multi-value-button"
        />
      </Tooltip>
    </>
  );
}
